import type { CertificateResource, EnvironmentV2Resource, Repository, TenantResource } from "@octopusdeploy/octopus-server-client";
import { getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, Permission, TenantedDeploymentMode } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { CertificateDetailsFormSections } from "~/areas/library/components/Certificates/Certificate/CertificateDetailsFormSections";
import { FinaliseCertificateCallout } from "~/areas/library/components/Certificates/Certificate/FinaliseCertificateCallout";
import { NewCertificateDetailsFormSections } from "~/areas/library/components/Certificates/Certificate/NewCertificateDetailsFormSections";
import type { UploadFormatOptions } from "~/areas/library/components/Certificates/Certificate/UploadFormatOptions";
import { createSaveCertificateAnalyticsEvent } from "~/areas/library/components/Certificates/Certificate/amplitudeAnalytics";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Form, useForm } from "~/components/form/Form/Form";
import { TenantsOrTenantTagsSelectedOnUntenantedDeploymentMode } from "~/utils/TenantedDeploymentParticipationHelper/TenantsOrTenantTagsSelectedOnUntenantedDeploymentMode";
import type { AddCertificateWorkflowInput } from "~/workflows/AddCertificateWorkflowInput";
import { WorkflowContent } from "~/workflows/WorkflowContent";
import { workflows } from "~/workflows/workflows";
export async function addCertificateWorkflowLoader(repository: Repository, input: AddCertificateWorkflowInput): Promise<AddCertificateWorkflowLoaderData> {
    const allTenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
    const allEnvironments = getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(input.ephemeralEnvironmentsAreEnabled, repository);
    return {
        allTenants: await allTenants,
        allEnvironments: await allEnvironments,
    };
}
interface AddCertificateWorkflowLoaderData {
    allTenants: TenantResource[];
    allEnvironments: EnvironmentV2Resource[];
}
interface AddCertificateWorkflowProps {
    input: AddCertificateWorkflowInput;
    completeWorkflow: (output: CertificateResource) => void;
    loaderData: AddCertificateWorkflowLoaderData;
}
interface AddCertificateWorkflowModel {
    uploadFormat: UploadFormatOptions;
    certificate: CertificateResource;
}
export function AddCertificateWorkflow({ input: { spaceId, initiallySelectedTenantIds }, completeWorkflow, loaderData }: AddCertificateWorkflowProps) {
    const trackEvent = useTrackEvent();
    const { formProps, model, setModel, getFieldError, createSaveAction } = useForm({
        formName: "Add Certificate",
        initialModel: createInitialModel(initiallySelectedTenantIds),
        onSubmit: async (repository, model) => {
            const savedCertificate = model.uploadFormat === "SelfSigned" ? await repository.Certificates.saveSelfSigned(model.certificate) : await repository.Certificates.save(model.certificate);
            return { certificate: savedCertificate, uploadFormat: model.uploadFormat };
        },
        updateFormModelAfterSubmit: (model) => model,
        afterSubmit: async (_, updatedModel) => {
            trackEvent(createSaveCertificateAnalyticsEvent());
            completeWorkflow(updatedModel.certificate);
        },
        validate: (model) => {
            if (TenantsOrTenantTagsSelectedOnUntenantedDeploymentMode(model.certificate)) {
                return {
                    errorMessage: "Tenanted deployment mode",
                    fieldErrors: { TenantedDeploymentParticipation: "Please remove any associated tenants or tenant tags to use Untenanted deployment mode." },
                };
            }
        },
        snackbarSaveText: "New Certificate created",
    });
    const { doBusyTask, status } = useLegacyDoBusyTask();
    return (<WorkflowContent.Drawer workflow={workflows.addCertificateWorkflow} title={addCertificateWorkflowTitle} primaryAction={createSaveAction({ enabledWhenClean: true })} legacyDoBusyTaskStatus={status}>
            <Form {...formProps} expandAllOnMount={true}>
                <FinaliseCertificateCallout spaceId={spaceId}/>
                <CertificateDetailsFormSections model={model.certificate} setModelState={(c) => setModel((prev) => ({ ...prev, certificate: { ...prev.certificate, ...c } }))} getFieldError={getFieldError} allTenants={loaderData.allTenants} allEnvironments={loaderData.allEnvironments} doBusyTask={doBusyTask} certificateDetails={<NewCertificateDetailsFormSections model={model.certificate} setModelState={(c) => setModel((prev) => ({ ...prev, certificate: { ...prev.certificate, ...c } }))} uploadFormat={model.uploadFormat} setUploadFormat={(uploadFormat) => setModel((prev) => ({ ...prev, uploadFormat }))} getFieldError={getFieldError}/>}/>
            </Form>
        </WorkflowContent.Drawer>);
}
function createInitialModel(initiallySelectedTenantIds: string[]): AddCertificateWorkflowModel {
    return {
        uploadFormat: "File",
        certificate: {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            Id: null!,
            Name: "",
            Notes: "",
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            CertificateData: { NewValue: null!, HasValue: false },
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            Password: { NewValue: null!, HasValue: false },
            EnvironmentIds: [],
            TenantIds: initiallySelectedTenantIds,
            TenantTags: [],
            TenantedDeploymentParticipation: initiallySelectedTenantIds.length > 0 ? TenantedDeploymentMode.Tenanted : TenantedDeploymentMode.Untenanted,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            Links: null!,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            SpaceId: null!,
        },
    };
}
export const addCertificateWorkflowTitle = "Add Certificate";
